import { accountKey } from '../hierarchy/buildClusterTopLevel';
import type { ExplorationVolume } from '../../types/exploration';
import { clusterTopForVolume, clusterTopKeyString } from '../drill/drillLocation';

function clusterTopKey(v: ExplorationVolume): string {
  return clusterTopKeyString(clusterTopForVolume(v));
}

export function isKnownAccountName(name: string, volumes: ExplorationVolume[]): boolean {
  if (!name || !volumes.length) return false;
  // When a segment matches a cluster/EC2 top key, treat it as structural — not account —
  // even if accountKey(v) collides (common for EKS cluster names in org-wide drills).
  // Tradeoff: a real account whose name equals some cluster name in the dataset will not
  // be stripped as an account prefix until volumes for that account no longer include
  // that cluster top key.
  if (volumes.some((v) => clusterTopKey(v) === name)) return false;
  const known = new Set(volumes.map(accountKey));
  return known.has(name);
}

export interface ResolvedAccountDrillPrefix {
  accountName: string | null;
  path: string[];
}

/** Split account prefix from drill path when groupByAccount is on and the first segment is a real account. */
export function resolveAccountDrillPrefix(
  drillPath: string[],
  groupByAccount: boolean,
  volumes: ExplorationVolume[]
): ResolvedAccountDrillPrefix {
  if (!groupByAccount || drillPath.length === 0) {
    return { accountName: null, path: drillPath };
  }

  const first = drillPath[0];
  if (isKnownAccountName(first, volumes)) {
    return { accountName: first, path: drillPath.slice(1) };
  }

  return { accountName: null, path: drillPath };
}

/**
 * Account prefix for tooltip ancestry chips.
 * Validates against volumes when available; otherwise assumes the first segment is the account.
 */
export function resolveAccountAncestryPrefix(
  path: string[],
  groupByAccount: boolean,
  volumes?: ExplorationVolume[]
): ResolvedAccountDrillPrefix {
  if (!groupByAccount || !path.length) {
    return { accountName: null, path };
  }
  if (volumes?.length) {
    return resolveAccountDrillPrefix(path, true, volumes);
  }
  return { accountName: path[0], path: path.slice(1) };
}
